import { formatToTimeZone } from './Format';
import { IsoDate, IsoDateFormat, assertIsoDate } from './IsoDate';
import IsoTimestamp, { assertIsoTimestamp } from './IsoTimestamp';
import getTZAbbr from './getTZAbbr';
import {
  add,
  addMilliseconds as dateFnsAddMilliseconds,
  addMinutes as dateFnsAddMinutes,
  addBusinessDays as dateFnsAddBusinessDays,
  endOfDay as dateFnsEndOfDay,
  endOfMonth as dateFnsEndOfMonth,
  endOfWeek as dateFnsEndOfWeek,
  startOfDay as dateFnsStartOfDay,
  startOfMonth as dateFnsStartOfMonth,
  startOfWeek as dateFnsStartOfWeek,
  subYears as dateFnsSubYears,
  isSameMonth as dateFnsIsSameMonth,
  parseISO,
  daysToWeeks,
} from 'date-fns';
import { format, getTimezoneOffset, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { secondsInDay } from 'date-fns/constants';

export const startOfDay = (dateInput: IsoTimestamp, tz: string): IsoTimestamp => {
  const date = toZonedTime(dateInput, tz);
  const start = dateFnsStartOfDay(date);
  return assertIsoTimestamp(fromZonedTime(start, tz).toISOString());
};

export const startOfWeek = (dateInput: IsoTimestamp, tz: string): IsoTimestamp => {
  const date = toZonedTime(dateInput, tz);
  const start = dateFnsStartOfWeek(date);
  return assertIsoTimestamp(fromZonedTime(start, tz).toISOString());
};

export const startOfWeekFromDate = (dateInput: IsoDate): IsoDate => {
  const start = dateFnsStartOfWeek(new Date(dateInput), { weekStartsOn: 0 });
  return assertIsoDate(format(start, IsoDateFormat));
};

export const endOfDay = (dateInput: IsoTimestamp, tz: string): IsoTimestamp => {
  const date = toZonedTime(dateInput, tz);
  const end = dateFnsEndOfDay(date);
  return assertIsoTimestamp(fromZonedTime(end, tz).toISOString());
};

export const endOfWeek = (dateInput: IsoTimestamp, tz: string): IsoTimestamp => {
  const date = toZonedTime(dateInput, tz);
  const end = dateFnsEndOfWeek(date);
  return assertIsoTimestamp(fromZonedTime(end, tz).toISOString());
};

export const endOfWeekFromDate = (dateInput: IsoDate): IsoDate => {
  const end = dateFnsEndOfWeek(new Date(dateInput), { weekStartsOn: 0 });
  return assertIsoDate(format(end, IsoDateFormat));
};

export const addMinutes = (dateInput: IsoTimestamp, tz: string, minutes: number): IsoTimestamp => {
  const zonedTime = toZonedTime(dateInput, tz);
  const newTime = dateFnsAddMinutes(zonedTime, minutes);
  return assertIsoTimestamp(fromZonedTime(newTime, tz).toISOString());
};

/**
 * This is a weird function because it doesn't simply add the days to the date but it also sets the start of day of the date.
 * @deprecated Use addDays instead and then startOfDay on the result if you need to.
 * @param dateInput - The date to add days to.
 * @param tz - The timezone to add days to.
 * @param days - The number of days to add.
 * @returns The date after adding the days but the start of day of that date.
 */
export const addDaysAndSetStartOfDay_DEPRECATED = (dateInput: IsoTimestamp, tz: string, days: number): IsoTimestamp => {
  const date = startOfDay(dateInput, tz);
  return assertIsoTimestamp(add(parseISO(date), { days }).toISOString());
};

export const subYears = (dateInput: IsoTimestamp, tz: string, years: number): IsoTimestamp => {
  const date = startOfDay(dateInput, tz);
  return assertIsoTimestamp(dateFnsSubYears(date, years).toISOString());
};

export const addDaysToDate = (dateInput: IsoDate, days: number): IsoDate => {
  // add days starting from IsoDate
  const newDate = add(parseISO(dateInput), { days });
  return assertIsoDate(format(newDate, IsoDateFormat));
};

/**
 * This is a weird function because it doesn't simply add the weeks to the date but it also sets the start of day of the date.
 * @deprecated Use addWeeks instead and then startOfDay on the result if you need to.
 * @param dateInput - The date to add weeks to.
 * @param tz - The timezone to add weeks to.
 * @param weeks - The number of weeks to add.
 * @returns The date after adding the weeks but the start of day of that date.
 */
export const addWeeksAndSetStartOfDay_DEPRECATED = (
  dateInput: IsoTimestamp,
  tz: string,
  weeks: number
): IsoTimestamp => {
  const date = startOfDay(dateInput, tz);
  return assertIsoTimestamp(add(parseISO(date), { weeks }).toISOString());
};

/**
 * This is a weird function because it doesn't simply add the months to the date but it also sets the start of day of the date.
 * @deprecated Use addMonths instead and then startOfDay on the result if you need to.
 * @param dateInput - The date to add months to.
 * @param tz - The timezone to add months to.
 * @param months - The number of months to add.
 * @returns The date after adding the months but the start of day of that date.
 */
export const addMonthsAndSetStartOfDay_DEPRECATED = (
  dateInput: IsoTimestamp,
  tz: string,
  months: number
): IsoTimestamp => {
  const date = startOfDay(dateInput, tz);
  return assertIsoTimestamp(add(parseISO(date), { months }).toISOString());
};

export const startOfMonth = (dateInput: IsoTimestamp, tz: string): IsoTimestamp => {
  const date = toZonedTime(dateInput, tz);
  const start = dateFnsStartOfMonth(date);
  return assertIsoTimestamp(fromZonedTime(start, tz).toISOString());
};

export const endOfMonth = (dateInput: IsoTimestamp, tz: string): IsoTimestamp => {
  const date = toZonedTime(dateInput, tz);
  const start = dateFnsEndOfMonth(date);
  return assertIsoTimestamp(fromZonedTime(start, tz).toISOString());
};

/**
 * If the local time object passed has 14:30 then it will return time which translates to 14:30 in the timezone.
 * E.g. time ('2021-12-12T09:00:00.000Z') represents 2:30 PM in 'Asia/Kolkata' (local time) and timezone is 'America/Los_Angeles'
 *      then it will return ('2021-12-12T22:30:00.000Z') which is 2:30 PM in 'America/Los_Angeles'
 * @param time in local timezone
 * @param timezone to which the time needs to be returned in.
 * @param hours the hour value of the time returned.
 * @param minutes the minute value of the time returned.
 * @returns Returns the time in the timezone represented by hours and minutes.
 */
export const getTimeInTimezone = (
  time: IsoTimestamp,
  timezone: string,
  hours?: number,
  minutes?: number
): IsoTimestamp => {
  const newDate = parseISO(time);
  const result = new Date(Date.UTC(newDate.getFullYear(), newDate.getMonth(), newDate.getDate()));
  result.setUTCHours(hours ?? newDate.getHours(), minutes ?? newDate.getMinutes(), 0, 0);
  return assertIsoTimestamp(dateFnsAddMilliseconds(result, -getTimezoneOffset(timezone, result)).toISOString());
};

export const isSameDay = (d1: IsoTimestamp, d2: IsoTimestamp, tz: string) => {
  return (
    format(toZonedTime(d1, tz), IsoDateFormat, { timeZone: tz }) ===
    format(toZonedTime(d2, tz), IsoDateFormat, { timeZone: tz })
  );
};

export const isSameMonth = (d1: IsoTimestamp, d2: IsoTimestamp, tz: string) => {
  return dateFnsIsSameMonth(toZonedTime(d1, tz), toZonedTime(d2, tz));
};

export const getUniqueDays = (dates: IsoTimestamp[], tz: string) => {
  const uniqueStartOfDays: IsoTimestamp[] = [];
  dates.forEach((date) => {
    const start = startOfDay(date, tz);
    if (uniqueStartOfDays.includes(start)) {
      return;
    }
    uniqueStartOfDays.push(start);
  });

  return uniqueStartOfDays;
};

export const addBusinessDays = (dateInput: IsoTimestamp, tz: string, days: number): IsoTimestamp => {
  const zonedTime = toZonedTime(dateInput, tz);
  const newDate = dateFnsAddBusinessDays(zonedTime, days);
  const utcTime = fromZonedTime(newDate, tz);
  return assertIsoTimestamp(utcTime.toISOString());
};

/**
 *  10:30 am - 12 pm
 *  12 - 12:30 pm
 *  9:45 - 10:30 am
 */
export const getEventTimeRange = (
  startAt: IsoTimestamp,
  endAt: IsoTimestamp,
  tz: string,
  options?: {
    showFullTime: boolean;
    showTimezoneAbbreviation: boolean;
    useUppercaseMerridian: boolean;
  }
): string => {
  const timeOptions = options ?? {
    showFullTime: false,
    showTimezoneAbbreviation: true,
    useUppercaseMerridian: true,
  };
  const sameMeridiem = formatToTimeZone(startAt, 'a', tz) === formatToTimeZone(endAt, 'a', tz);
  const startHasMinutes = formatToTimeZone(startAt, 'mm', tz) !== '00';
  const endHasMinutes = formatToTimeZone(endAt, 'mm', tz) !== '00';
  const merridianFormat = timeOptions.useUppercaseMerridian ? 'a' : 'aaa';
  const startFormat =
    startHasMinutes || timeOptions.showFullTime
      ? `h:mm${sameMeridiem ? '' : ` ${merridianFormat}`}`
      : `h${sameMeridiem ? '' : ` ${merridianFormat}`}`;
  const endFormat = endHasMinutes || timeOptions.showFullTime ? `h:mm ${merridianFormat}` : `h ${merridianFormat}`;
  return `${formatToTimeZone(startAt, startFormat, tz)} - ${formatToTimeZone(endAt, endFormat, tz)} ${
    timeOptions.showTimezoneAbbreviation ? getTZAbbr(tz) : ''
  }`;
};

// helper funtion for adding milliseconds to a date
export const addMilliseconds = (dateInput: IsoTimestamp, tz: string, milliseconds: number): IsoTimestamp => {
  const zonedTime = toZonedTime(dateInput, tz);
  const newDate = dateFnsAddMilliseconds(zonedTime, milliseconds);
  const utcTime = fromZonedTime(newDate, tz);
  return assertIsoTimestamp(utcTime.toISOString());
};

export const secondsToWeeks = (seconds: number): number | undefined => {
  return daysToWeeks(seconds / secondsInDay);
};
